import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
import { CPC_STAKE_LOCK_EPOCHS } from './buildStakeWithdraw.js';
import { mistToDgc } from './amount.js';
import { estimateFungibleStakedCpc, estimateStakedSuiNextClaimMist, } from './fungibleStakedValue.js';
const STAKED_SUI_TYPE = `${SUI_SYSTEM_ADDRESS}::staking_pool::StakedSui`;
const FUNGIBLE_STAKED_SUI_TYPE = `${SUI_SYSTEM_ADDRESS}::staking_pool::FungibleStakedSui`;
function fieldU64(fields, key) {
    const v = fields?.[key];
    if (v == null)
        return null;
    return String(v);
}
export async function fetchStakeLockUntilEpoch(client, stakedSuiId, stakeRequestEpoch) {
    try {
        const res = await client.getObject({ id: stakedSuiId, options: { showContent: true } });
        const fields = res.data?.content?.fields;
        const lock = fieldU64(fields, 'lock_until_epoch');
        if (lock != null)
            return lock;
    }
    catch {
        /* object may be consumed after convert */
    }
    return String(Number(stakeRequestEpoch) + CPC_STAKE_LOCK_EPOCHS);
}
function principalLockMetrics(currentEpoch, stakeActiveEpoch, lockUntilEpoch, principalLocked) {
    if (!principalLocked) {
        return { epochsUntilPrincipalUnlock: 0, principalLockProgressPercent: 100 };
    }
    const until = Math.max(0, lockUntilEpoch - currentEpoch);
    const span = Math.max(1, lockUntilEpoch - stakeActiveEpoch);
    const elapsed = Math.max(0, currentEpoch - stakeActiveEpoch);
    const principalLockProgressPercent = Math.min(99, Math.round((elapsed / span) * 100));
    return { epochsUntilPrincipalUnlock: until, principalLockProgressPercent };
}
export async function listStakePositions(client, owner) {
    const system = await client.getLatestSuiSystemState();
    const currentEpoch = String(system.epoch);
    const currentNum = Number(currentEpoch);
    const out = [];
    const poolMeta = new Map();
    const poolToValidator = new Map();
    for (const v of system.activeValidators ?? []) {
        if (v.stakingPoolId && v.suiAddress) {
            poolToValidator.set(v.stakingPoolId, v.suiAddress);
        }
    }
    const delegated = await client.getStakes({ owner });
    for (const d of delegated) {
        for (const s of d.stakes) {
            if (s.status === 'Unstaked')
                continue;
            const lockUntilEpoch = await fetchStakeLockUntilEpoch(client, s.stakedSuiId, s.stakeRequestEpoch);
            const lockNum = Number(lockUntilEpoch);
            const principalLocked = currentNum < lockNum;
            const activeNum = Number(s.stakeActiveEpoch);
            const lockMetrics = principalLockMetrics(currentNum, activeNum, lockNum, principalLocked);
            let rewardCpc = '0';
            if (s.status === 'Active') {
                const rewardMist = await estimateStakedSuiNextClaimMist(client, d.stakingPool, BigInt(s.principal), activeNum, currentNum);
                if (rewardMist != null) {
                    rewardCpc = mistToDgc(rewardMist);
                }
            }
            const canClaimRewards = s.status === 'Active' && (rewardCpc ? Number(rewardCpc) > 0 : false);
            const prev = poolMeta.get(d.stakingPool);
            if (!prev || lockNum > Number(prev.lockUntilEpoch)) {
                poolMeta.set(d.stakingPool, {
                    validatorAddress: d.validatorAddress,
                    lockUntilEpoch,
                });
            }
            out.push({
                kind: 'staked_sui',
                validatorAddress: d.validatorAddress,
                stakedSuiId: s.stakedSuiId,
                principalCpc: mistToDgc(s.principal),
                principalMist: String(s.principal),
                status: s.status,
                stakeActiveEpoch: s.stakeActiveEpoch,
                stakeRequestEpoch: s.stakeRequestEpoch,
                lockUntilEpoch,
                currentEpoch,
                principalLocked,
                ...lockMetrics,
                estimatedRewardCpc: rewardCpc ?? '0',
                canClaimRewards,
                canRedeemPrincipal: false,
            });
        }
    }
    const fssItems = [];
    let fssCursor = null;
    do {
        const fssOwned = await client.getOwnedObjects({
            owner,
            filter: { StructType: FUNGIBLE_STAKED_SUI_TYPE },
            options: { showContent: true },
            cursor: fssCursor,
            limit: 50,
        });
        fssItems.push(...(fssOwned.data ?? []));
        fssCursor = fssOwned.hasNextPage ? fssOwned.nextCursor : null;
    } while (fssCursor);
    for (const item of fssItems) {
        const id = item.data?.objectId;
        if (!id)
            continue;
        let fields = item.data?.content?.fields;
        if (!fields?.pool_id && !fields?.value) {
            try {
                const full = await client.getObject({ id, options: { showContent: true } });
                fields = full.data?.content?.fields;
            }
            catch {
                /* */
            }
        }
        const poolId = fieldU64(fields, 'pool_id') ?? '';
        const value = fieldU64(fields, 'value') ?? '0';
        let principalCpc = '—';
        let principalMist = '0';
        let stakeActiveEpoch = '—';
        let stakeRequestEpoch = '—';
        let estimatedRewardCpc = '0';
        let positionValueCpc;
        let lockUntilFromChain;
        let stakeActiveNum = null;
        try {
            const est = await estimateFungibleStakedCpc(client, poolId, value, currentNum, id);
            // Only the chain redeem split for this certificate — never pool-wide principal
            // or token-market-value (those made 本金 jump after claim).
            if (est.userPrincipalMist != null) {
                principalCpc = mistToDgc(est.userPrincipalMist);
                principalMist = String(est.userPrincipalMist);
            }
            if (est.estimatedTotalCpcMist > 0n) {
                positionValueCpc = mistToDgc(est.estimatedTotalCpcMist);
            }
            if (est.lockUntilEpoch != null) {
                lockUntilFromChain = String(est.lockUntilEpoch);
            }
            if (est.stakeActivationEpoch != null) {
                stakeActiveEpoch = String(est.stakeActivationEpoch);
                stakeRequestEpoch = stakeActiveEpoch;
                stakeActiveNum = Number(est.stakeActivationEpoch);
            }
            if (est.estimatedUnclaimedRewardMist != null) {
                estimatedRewardCpc = mistToDgc(est.estimatedUnclaimedRewardMist);
            }
        }
        catch {
            /* keep defaults */
        }
        const meta = poolMeta.get(poolId);
        const lockUntilEpoch = lockUntilFromChain ?? meta?.lockUntilEpoch ?? '—';
        if (stakeActiveNum == null && lockUntilEpoch !== '—') {
            // FungibleStakedSuiData has no activation epoch; infer from fixed 180-epoch lock.
            stakeActiveNum = Math.max(0, Number(lockUntilEpoch) - CPC_STAKE_LOCK_EPOCHS + 1);
            stakeActiveEpoch = String(stakeActiveNum);
            stakeRequestEpoch = String(Math.max(0, stakeActiveNum - 1));
        }
        const lockNum = lockUntilEpoch === '—' ? currentNum + CPC_STAKE_LOCK_EPOCHS : Number(lockUntilEpoch);
        const activeForMetrics = stakeActiveNum ?? currentNum;
        const principalLocked = lockUntilEpoch === '—' || currentNum < lockNum;
        const lockMetrics = principalLockMetrics(currentNum, activeForMetrics, lockNum, principalLocked);
        out.push({
            kind: 'fungible_staked_sui',
            validatorAddress: poolToValidator.get(poolId) ?? meta?.validatorAddress ?? poolId,
            fungibleStakedSuiId: id,
            principalCpc,
            principalMist,
            positionValueCpc,
            poolTokenAmount: value,
            principalDisplayNote: '本金（质押锁定，提取收益不变）',
            status: 'Fungible',
            stakeActiveEpoch,
            stakeRequestEpoch,
            lockUntilEpoch,
            currentEpoch,
            principalLocked,
            ...lockMetrics,
            estimatedRewardCpc,
            canClaimRewards: Number(estimatedRewardCpc) > 0,
            canRedeemPrincipal: !principalLocked,
        });
    }
    return out;
}
export { STAKED_SUI_TYPE, FUNGIBLE_STAKED_SUI_TYPE };
